import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';

export interface SchoolSummary {
  id: string;
  name?: string;
  academy?: string;
  cap?: string;
  region?: string;
  commune?: string;
  address?: string;
  status?: string;
  responsibleIds?: string[];
  createdAt?: Date;
  updatedAt?: Date;
}

const COLLECTION = 'schools';

export async function listSchoolsSummary(): Promise<SchoolSummary[]> {
  const snap = await getDocs(
    query(collection(db, COLLECTION), orderBy('name', 'asc'))
  );

  return snap.docs.map((d) => {
    const data = d.data() as any;
    return {
      id: d.id,
      name: data.name,
      academy: data.academy,
      cap: data.cap,
      region: data.region,
      commune: data.commune,
      address: data.address,
      status: data.status,
      responsibleIds: Array.isArray(data.responsibleIds)
        ? (data.responsibleIds as string[])
        : [],
      createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : undefined,
      updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : undefined,
    };
  });
}

/**
 * Assigne un responsable à une liste d'écoles en l'ajoutant au tableau
 * `responsibleIds` de chaque école (sans écraser les responsables existants).
 */
export async function assignResponsibleToSchools(
  responsibleId: string,
  schoolIds: string[]
): Promise<void> {
  if (!responsibleId || !schoolIds.length) return;
  const batch = writeBatch(db);
  schoolIds.forEach((schoolId) => {
    const ref = doc(db, COLLECTION, schoolId);
    batch.update(ref, {
      responsibleIds: arrayUnion(responsibleId),
      updatedAt: new Date(),
    });
  });
  await batch.commit();
}

export async function toggleSchoolStatus(
  id: string,
  current?: string
): Promise<void> {
  const ref = doc(db, COLLECTION, id);
  const next = current === 'active' ? 'inactive' : 'active';
  await updateDoc(ref, { status: next });
}

export async function updateSchool(
  id: string,
  payload: {
    name: string;
    academy: string;
    cap: string;
    region: string;
    commune: string;
    address: string;
    status: string;
  }
): Promise<void> {
  const ref = doc(db, COLLECTION, id);
  await updateDoc(ref, {
    name: payload.name.trim(),
    academy: payload.academy.trim() || null,
    cap: payload.cap.trim() || null,
    region: payload.region.trim() || null,
    commune: payload.commune.trim() || null,
    address: payload.address.trim() || null,
    status: payload.status,
    updatedAt: new Date(),
  });
}

export async function createSchool(payload: {
  name: string;
  academy: string;
  cap: string;
  region: string;
  commune: string;
  address: string;
  status: string;
}): Promise<string> {
  const ref = await addDoc(collection(db, COLLECTION), {
    name: payload.name.trim(),
    academy: payload.academy.trim() || null,
    cap: payload.cap.trim() || null,
    region: payload.region.trim() || null,
    commune: payload.commune.trim() || null,
    address: payload.address.trim() || null,
    status: payload.status,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

